import { useCallback, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/features';
import { useActionUtils } from '@/hooks/use-action-utils';
import { type Notification } from '@/types/swagger';
import { Button, Divider, List } from '@mui/material';
import { Link } from '@tanstack/react-router';
import { fetchNotifications, markNotificationAsSeen } from 'features/Notifications';
import moment from 'moment';
import IntlMessages from 'util/IntlMessages';

import NotificationItem from '../NotificationItem';

export interface NotificationsContentProps {
  size: 'small' | 'medium' | 'large';
  filter?: string;
  closeNotifications?: () => void;
}

const NotificationsContent: React.FC<NotificationsContentProps> = ({
  size,
  closeNotifications,
  filter,
}) => {
  const dispatch = useAppDispatch();
  const { getActionPath } = useActionUtils();
  const inAppSubscriptions = useAppSelector((state) => state.notifications.inAppSubscriptions);
  const emailSubscriptions = useAppSelector((state) => state.notifications.emailSubscriptions);
  const notifications = useAppSelector((state) => state.notifications.notifications);
  const page = useAppSelector((state) => state.notifications.page);

  const [unreadOnly, _setUnreadOnly] = useState(false);
  const [isFetching, setisFetching] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);

  const sortedNotifications = useMemo(() => {
    let _sortedNotifications: Notification[] = [];

    _sortedNotifications = [...notifications].sort((a, b) => {
      return moment.utc(a.created).isBefore(moment.utc(b.created)) ? 1 : -1;
    });

    if (filter) {
      _sortedNotifications = _sortedNotifications.filter((n) => {
        return getActionPath(n.action_code).includes(filter);
      });
    }
    return unreadOnly ? _sortedNotifications.filter((n) => !n.seen) : _sortedNotifications;
  }, [notifications, unreadOnly, filter, getActionPath]);

  const todayNotifications = useMemo(
    () =>
      sortedNotifications.filter((notification) =>
        moment(notification.created).local().isSame(moment().local(), 'day')
      ),
    [sortedNotifications]
  );

  const olderNotifications = useMemo(
    () =>
      sortedNotifications.filter(
        (notification) => !moment(notification.created).local().isSame(moment().local(), 'day')
      ),
    [sortedNotifications]
  );

  const handleLazyLoading = useCallback(async () => {
    if (
      !isFetching &&
      page &&
      listRef.current &&
      listRef.current.scrollTop + listRef.current.clientHeight + 1 > listRef.current.scrollHeight
    ) {
      setisFetching(true);
      await dispatch(fetchNotifications({ page, pageSize: 10 }));
      setisFetching(false);
    }
  }, [dispatch, isFetching, page]);

  const handleMarkAllAsSeen = useCallback(async () => {
    const notificationsIds = notifications.map((notification) => notification.id);
    notificationsIds.forEach((id, i) => {
      setTimeout(() => {
        void dispatch(markNotificationAsSeen(id));
      }, i * 50);
    });
  }, [dispatch, notifications]);

  return (
    <>
      <div className="d-flex container col-12 notifications-header p-0 py-3 font-size-20 justify-content-between align-items-center px-md-3 px-3 font-size-28">
        <p className={`p-0 m-0 ${size === 'large' ? 'col-8' : 'col-6'}`}>
          <IntlMessages id="common.labels.notifications" />
        </p>
      </div>
      <div className="notifications-content">
        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.5)' }} />
        <div
          ref={listRef}
          className={`quick-notifications-list${size === 'large' ? 'large' : ''}`}
          onScroll={handleLazyLoading}
        >
          <List
            sx={{ display: 'flex' }}
            className="d-flex flex-column w-100 justify-content-center align-items-start px-1"
          >
            {sortedNotifications.length ? (
              todayNotifications.length ? (
                <>
                  <div className="d-flex flex-row w-100 pr-5 m-0">
                    <p
                      className={`quick-notifications-time-title col-4 ${
                        size === 'large' ? 'pl-4' : 'pl-2'
                      }`}
                    >
                      <IntlMessages id="pages.notifications-quick-view.today" />
                    </p>
                    {notifications.some((n) => !n.seen) ? (
                      <button
                        type="button"
                        onClick={handleMarkAllAsSeen}
                        className={`mark-all-as-read-button col-8 m-3${
                          size === 'small' ? 'font-size-14' : ''
                        }`}
                      >
                        <IntlMessages id="pages.notifications-quick-view.mark-all-read" />
                      </button>
                    ) : null}
                  </div>
                  {todayNotifications.map((notification) => (
                    <NotificationItem
                      key={`notification-${notification.id}`}
                      notification={notification}
                      size={size}
                    />
                  ))}
                  {olderNotifications.length ? (
                    <>
                      <p
                        className={`quick-notifications-time-title ${
                          size === 'large' ? 'pl-4' : 'pl-2'
                        }`}
                      >
                        <IntlMessages id="pages.notifications-quick-view.older" />
                      </p>
                      {olderNotifications.map((notification) => (
                        <NotificationItem
                          key={`notification-${notification.id}`}
                          notification={notification}
                          size={size}
                        />
                      ))}
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  <div className="d-flex flex-row w-100 pr-5 m-0">
                    <p className="quick-notifications-time-title col-6">
                      <IntlMessages id="pages.notifications-quick-view.older" />
                    </p>
                    <button
                      type="button"
                      onClick={handleMarkAllAsSeen}
                      className="mark-all-as-read-button col-6"
                      disabled={notifications.every((notification) => notification.seen)}
                    >
                      <IntlMessages id="pages.notifications-quick-view.mark-all-read" />
                    </button>
                  </div>
                  {olderNotifications.map((notification) => (
                    <NotificationItem
                      key={`notification-${notification.id}`}
                      notification={notification}
                      size={size}
                    />
                  ))}
                </>
              )
            ) : inAppSubscriptions.length === 0 && emailSubscriptions.length === 0 ? (
              <div className="d-flex flex-column w-100 justify-content-center align-items-center">
                <p className="w-100 py-3 d-flex justify-content-center">
                  <IntlMessages id="pages.notifications-quick-view.no-subscriptions" />
                </p>
                <Link
                  to="/preferences"
                  className="no-text-decoration color-white w-100 d-flex justify-content-center"
                >
                  <Button onClick={closeNotifications}>
                    <IntlMessages id="pages.notifications-quick-view.go-to-settings" />
                  </Button>
                </Link>
              </div>
            ) : unreadOnly ? (
              <p className="w-100 py-3 d-flex justify-content-center text-center">
                <IntlMessages id="pages.notifications-quick-view.no-unread-notifications" />
              </p>
            ) : (
              <p className="w-100 py-3 d-flex justify-content-center text-center">
                <IntlMessages id="pages.notifications-quick-view.no-notifications" />
              </p>
            )}
          </List>
        </div>
      </div>
    </>
  );
};

export default NotificationsContent;
